/*
 * Use localStorage or sessionStorage as a cache, based on MapCache.
 * NOTE: This may not support proper LRU as it all depends on the browser to store the data
 *       in proper order. Limited to 10MB in size for most modern browsers.
 */
export default class StorageCache {
    constructor(limit = 10, storage = sessionStorage, db = 'db', type = 'session') {
        this.timer();
        this.limit = limit;
        this.storage = storage;
        this.prefix = `__cache:${db} `;
        this.tstamp = `_tstamp:${db} `;
        this.mechanism = type;
    }

    get isCache() {
        return true;
    }

    getKey(key, force = false) {
        return !force && this.hasPrefix(key) ? key : `${this.prefix}${key}`;
    }

    getTstamp(_key, value = false) {
        let key = _key.replace(new RegExp(`${this.prefix}`), ''),
            skey = `${this.tstamp}${key}`;

        return !value ? skey : new Date(parseInt(this.storage.getItem(skey)));
    }

    timer() {
        clearTimeout(this.pruneTimer);
        this.pruneTimer = setTimeout(() => this.prune(), 30000);

        return this;
    }

    set(key, value, duration) {
        this.storage.setItem(this.getTstamp(key), Date.now());
        this.storage.setItem(this.getKey(key), JSON.stringify({
            value,
            expires: duration ? Date.now() + duration : false,
            ttl: duration,
        }));

        return this.evict().has(key);
    }

    get(_key, data = true) {
        let key = this.getKey(_key);

        if (this.has(key)) {
            let obj = JSON.parse(this.storage.getItem(key) || '{}');

            return this.isExpired(obj) ?
                (this.storage.removeItem(key) && undefined)
                :
                (data ? obj.value : obj);
        }

        return undefined;
    }

    delete(key) {
        this.evict([
            this.getKey(key),
            this.getTstamp(key),
        ], false);

        return !this.has(key);
    }

    has(key = false) {
        return this.storage.getItem(this.getKey(key)) ? true : false;
    }

    clear() {
        this.evict([
            ...this.keys(false),
            ...this.stamps(false),
        ], false);
    }

    isExpired(entry = {}) {
        return entry.expires ? entry.expires <= Date.now() : false;
    }

    hasPrefix(key) {
        return String(key).indexOf(`${this.prefix}`) === 0;
    }

    stamps(clean = true) {
        return Object.keys(this.storage).filter(key => {
            return String(key).indexOf(`${this.tstamp}`) === 0
        })[clean ? 'map' : 'valueOf'](key => key.replace(new RegExp(`${this.tstamp}`), ''));
    }

    get size() {
        return Object.keys(this.storage).filter(key => this.hasPrefix(key)).length;
    }

    keys(clean = true) {
        return Object.keys(this.storage).filter(key => this.hasPrefix(key)).sort((a, b) => {
            return this.getTstamp(a, true) - this.getTstamp(b, true);
        })[clean ? 'map' : 'valueOf'](key => key.replace(new RegExp(`${this.prefix}`), ''));
    }

    values() {
        return this.keys().map(key => this.get(key));
    }

    entries() {
        return this.keys().map(key => [key, this.get(key)])
    }

    prune() {
        for (const [key, value] of this.entries()) {
            if (this.isExpired(value)) this.delete(this.getKey(key));
        }

        return this.timer();
    }

    evict(keys = false, getKey = true) {
        if (keys) {
            keys.forEach(key => this.storage.removeItem(getKey ? this.getKey(key) : key));
        } else {
            clearTimeout(this.evitTimer);
            this.evitTimer = setTimeout(() => {
                if (this.size > this.limit) {
                    this.keys(false).slice(0, -Math.abs(this.limit)).forEach(key => this.evict([
                        key,
                        this.getTstamp(key),
                    ], false));
                }
            }, 100);
        }

        return this;
    }
}
